import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { MAX_SIZE_MB } from "../../helper";

export const REGISTRAR_OFFICE_ID = "8bb63a5d82478e162e50b8f5";

const DESIGNATIONS = [
  "Registrar",
  "Deputy Registrar",
  "Assistant Registrar",
];

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "560px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    maxHeight: "90vh",
    overflow: "auto",
  },
};

const emptyForm = {
  name: "",
  designation: "Deputy Registrar",
  email: "",
  phone: "",
  order: "",
  img: "",
};

const fieldStyle = {
  width: "100%",
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontWeight: "bold",
  marginBottom: "5px",
};

const RegistrarTeamSection = () => {
  const [members, setMembers] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imgFile, setImgFile] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setFetchLoading(true);
    try {
      const response = await axiosInstance.get("office-team/all", {
        params: { officeId: REGISTRAR_OFFICE_ID },
      });
      setMembers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching registrar team:", error);
    }
    setFetchLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_SIZE_MB) {
      alert(`File size exceeds ${MAX_SIZE_MB} MB. Please upload a smaller file.`);
      e.target.value = "";
      return;
    }
    setImgFile(file);
  };

  const buildPayload = () => {
    const formDataToSend = new FormData();
    formDataToSend.append("officeId", REGISTRAR_OFFICE_ID);
    formDataToSend.append("name", formData.name);
    formDataToSend.append("designation", formData.designation);
    formDataToSend.append("email", formData.email || "");
    formDataToSend.append("phone", formData.phone || "");
    formDataToSend.append("order", String(Number(formData.order || 0)));
    if (imgFile) {
      formDataToSend.append("img", imgFile);
    }
    return formDataToSend;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      await axiosInstance.post("office-team/create", buildPayload());
      await fetchMembers();
      setShowCreateModal(false);
      setFormData(emptyForm);
      setImgFile(null);
      Swal.fire("Success!", "Team member added.", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to create",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      const formDataToSend = buildPayload();
      formDataToSend.append("_method", "PUT");
      await axiosInstance.post(`office-team/change/${editId}`, formDataToSend);
      await fetchMembers();
      setShowEditModal(false);
      setFormData(emptyForm);
      setImgFile(null);
      Swal.fire("Success!", "Team member updated.", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleView = async (id) => {
    try {
      setShowEditModal(true);
      setEditId(id);
      setImgFile(null);
      const response = await axiosInstance.get(`office-team/single/${id}`);
      const data = response?.data?.data;
      setFormData({
        name: data?.name || "",
        designation: data?.designation || "Deputy Registrar",
        email: data?.email || "",
        phone: data?.phone || "",
        order: data?.order ?? "",
        img: data?.img || "",
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load team member", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;

      await axiosInstance.delete(`office-team/delete/${id}`);
      await fetchMembers();
      Swal.fire("Deleted!", "Team member deleted.", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete",
        "error"
      );
    }
  };

  const renderForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          style={fieldStyle}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Designation</label>
        <select
          name="designation"
          value={formData.designation}
          onChange={handleChange}
          required
          style={fieldStyle}
        >
          {DESIGNATIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Email</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          style={fieldStyle}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Phone</label>
        <input
          type="text"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          style={fieldStyle}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Order</label>
        <input
          type="number"
          name="order"
          min="0"
          value={formData.order}
          onChange={handleChange}
          style={fieldStyle}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Photo</label>
        {formData.img ? (
          <img
            src={formData.img}
            alt=""
            style={{
              width: "100%",
              maxHeight: 140,
              objectFit: "cover",
              borderRadius: 6,
              marginBottom: 8,
            }}
          />
        ) : null}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={fieldStyle}
        />
      </div>
      <button
        type="submit"
        style={{
          padding: "10px 20px",
          backgroundColor: "#4CAF50",
          color: "#fff",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          marginTop: 10,
        }}
      >
        {buttonLoading ? <CircularProgress size={24} color="inherit" /> : submitLabel}
      </button>
    </form>
  );

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
        padding: 16,
        marginTop: 0,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <h3 style={{ margin: "0 0 4px" }}>Registrar Team</h3>
          <p style={{ margin: 0, color: "#666", fontSize: 13 }}>
            People shown on{" "}
            <code style={{ fontSize: 12 }}>/teamregistrar</code> — Registrar,
            Deputy Registrar, Assistant Registrar.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setFormData(emptyForm);
            setImgFile(null);
            setShowCreateModal(true);
          }}
          style={{
            padding: "8px 14px",
            backgroundColor: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 5,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Add Team Member
        </button>
      </div>

      {fetchLoading ? (
        <CircularProgress size={28} />
      ) : members.length === 0 ? (
        <p style={{ color: "#999", fontSize: 13, margin: 0 }}>
          No team members yet.
        </p>
      ) : (
        members
          .slice()
          .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
          .map((member) => (
            <div
              key={member._id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "10px 0",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {member.img ? (
                  <img
                    src={member.img}
                    alt=""
                    style={{
                      width: 48,
                      height: 48,
                      objectFit: "cover",
                      borderRadius: 6,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 6,
                      background: "#eee",
                    }}
                  />
                )}
                <div>
                  <div style={{ fontWeight: 600 }}>{member.name}</div>
                  <div style={{ fontSize: 13, color: "#555" }}>
                    {member.designation}
                    {member.email ? ` · ${member.email}` : ""}
                  </div>
                </div>
              </div>
              <Actions
                courseId={member._id}
                onEdit={handleView}
                onDelete={handleDelete}
              />
            </div>
          ))
      )}

      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        style={customModalStyles}
        contentLabel="Add Registrar Team Member"
      >
        <h2>Add Team Member</h2>
        {renderForm(handleCreate, "Create")}
      </Modal>

      <Modal
        isOpen={showEditModal}
        onRequestClose={() => {
          setFormData(emptyForm);
          setImgFile(null);
          setShowEditModal(false);
        }}
        style={customModalStyles}
        contentLabel="Edit Registrar Team Member"
      >
        <h2>Edit Team Member</h2>
        {renderForm(handleEdit, "Update")}
      </Modal>
    </div>
  );
};

export default RegistrarTeamSection;
